import React, { useState, useEffect, useRef, useImperativeHandle, forwardRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay, faPause, faHeart as faHeartSolid, faSpinner } from '@fortawesome/free-solid-svg-icons';
import Soundfont from 'soundfont-player';
import { Midi } from '@tonejs/midi';
import { DEFAULT_BPM } from '../constants';
import './AudioPlayer.css';

const AudioPlayer = forwardRef(({
  pieceDisplayName,
  midiBlob,
  isFavorited,
  selectedWork,
  addFavorite,
  removeFavorite,
  bpm: initialBpm,
  selectedPart,
  handlePartSelection,
  parts,
  selectedPiece,
  onCurrentTimeChange,
  onBpmChange,
  onNoteClick,
  isNotesLoading = false,
  fixed = true
}, ref) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioContext, setAudioContext] = useState(null);
  const [violin, setViolin] = useState(null);
  const [midiFile, setMidiFile] = useState(null);
  const [isMidiLoading, setIsMidiLoading] = useState(false);
  const [totalDuration, setTotalDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [bpm, setBpm] = useState(initialBpm || DEFAULT_BPM);
  const intervalRef = useRef(null);
  const totalDurationRef = useRef(totalDuration);
  const startTimeRef = useRef(null);
  const timeoutIds = useRef([]);
  var bpmScalingFactor = DEFAULT_BPM / bpm;
  const startDelay = 5;

  // Initialize Soundfont and MIDI file
  useEffect(() => {
    if (midiBlob) {
      setIsMidiLoading(true);
      const init = async () => {
        const arrayBuffer = await midiBlob.arrayBuffer();
        const midi = new Uint8Array(arrayBuffer);
        
        const context = new (window.AudioContext || window.webkitAudioContext)();
        setAudioContext(context);
        const violin = await Soundfont.instrument(context, 'violin');
        setViolin(violin);
        
        try {
          const midiFile = new Midi(midi);
          setMidiFile(midiFile);
        } catch (error) {
          console.error('Error parsing MIDI:', error);
        } finally {
          setIsMidiLoading(false);
        }
      };
      init();
    } else {
      setMidiFile(null);
      setTotalDuration(0);
      setIsMidiLoading(false);
    }
  }, [midiBlob]);

  // Calculate total duration when midiFile or bpm changes
  useEffect(() => {
    if (midiFile) {
      const totalTime = Math.max(...midiFile.tracks.map(track =>
        Math.max(...track.notes.map(note => (note.time + note.duration) * bpmScalingFactor))
      ));
      setTotalDuration(totalTime + startDelay);
    }
  }, [midiFile, bpmScalingFactor]);

  // Update the bpmScalingFactor when it changes
  useEffect(() => {
    bpmScalingFactor = DEFAULT_BPM / bpm;
  }, [bpm]);

  // Update the totalDuration ref when it changes
  useEffect(() => {
    totalDurationRef.current = totalDuration;
  }, [totalDuration]);

  const togglePlayPause = () => {
    console.log('called togglePlayPause with ', isPlaying);
    if (isPlaying) {
      pauseMidi();
    } else {
      playMidi();
    }
    setIsPlaying(!isPlaying);
  };

  // Update progress based on current playback time
  const startProgressUpdater = () => {
    console.log('called Start progress bar');
    clearInterval(intervalRef.current); // Clear any previous intervals

    intervalRef.current = setInterval(() => {
      const now = Date.now();
      const elapsedTime = Math.min((now - startTimeRef.current) / 1000, totalDurationRef.current); // Elapsed time in seconds

      setCurrentTime(elapsedTime);
      if (onCurrentTimeChange) onCurrentTimeChange(elapsedTime);
      const progressPercentage = (elapsedTime / totalDurationRef.current) * 100;
      setProgress(progressPercentage);

      // Stop the interval if playback reaches the end
      if (elapsedTime >= totalDurationRef.current) {
        clearInterval(intervalRef.current);
        setIsPlaying(false); // Set playback state to paused
      }
    }, 2);  // Update every 100ms
  };

  const playFromProgress = (newProgress) => {
    const newTime = (newProgress / 100) * totalDurationRef.current;
    setProgress(newProgress);
    setCurrentTime(newTime);
    if (onCurrentTimeChange) onCurrentTimeChange(newTime);
    // Stop any ongoing playback
    if (isPlaying && violin) {
      violin.stop(); // Stop current playback
    }
    if (midiFile && violin){
      const now = audioContext.currentTime;
      const adjustedStartTime = now - newTime + startDelay; // Adjust the start time based on the new time

      midiFile.tracks.forEach(track => {
        track.notes.forEach(note => {
          const noteStartTime = note.time * bpmScalingFactor; // Original note start time
    
          // Only play notes that should start after the new time
          if (noteStartTime >= newTime - startDelay) {
            const startTime = adjustedStartTime + noteStartTime;
            const timeoutId = setTimeout(() => {
              violin.play(note.name, startTime, { duration: note.duration * bpmScalingFactor });
            }, (noteStartTime - newTime) * 1000);  // Convert time to milliseconds
            timeoutIds.current.push(timeoutId);
          }
        });
      });
    }
    // Start the progress updater again to track from the new position
    startTimeRef.current = Date.now() - newTime * 1000;
    startProgressUpdater();
  };

  // Handle user dragging the progress bar
  const handleProgressChange = (e) => {
    const newProgress = e.target.value;
    pauseMidi();

    const newTime = (newProgress / 100) * totalDurationRef.current;
    setProgress(newProgress);
    setCurrentTime(newTime);
    if (onCurrentTimeChange) onCurrentTimeChange(newTime);
    if (isPlaying){
      playFromProgress(newProgress);
    };
  };

  // Function to play MIDI
  const playMidi = async () => {
    if (audioContext) {
      if (audioContext.state === 'suspended') {
        await audioContext.resume();
      }

      // Ensure that tracks exist in the loaded MIDI file
      if (midiFile.tracks && midiFile.tracks.length > 0) {
        playFromProgress(progress);
      } else {
        console.error("No tracks found in the MIDI file.");
        return;
      }
    }
  };

  // Function to pause MIDI playback
  const pauseMidi = () => {
    if (violin){
      violin.stop();
      console.log('called pause with timeoutids ', timeoutIds);
      timeoutIds.current.forEach(id => clearTimeout(id));  // Cancel each timeout
      timeoutIds.current = [];  // Reset the array after clearing timeouts
    }
    clearInterval(intervalRef.current);  // Stop progress updates
  };

  // This function will handle the BPM slider
  const handleBpmChange = (e) => {
    const newBpm = e.target.value;
    setBpm(newBpm);
    if (onBpmChange) onBpmChange(newBpm);
  };

  // Helper function to convert seconds to mm:ss format
  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
  };

  // Expose play and pause functions to parent component
  useImperativeHandle(ref, () => ({
    play: playMidi,
    pause: pauseMidi,
    togglePlayPause: togglePlayPause,
    handleNoteClick: (newTime) => {
      const wasPlaying = isPlaying;
      pauseMidi();
      newTime = newTime / ((bpm / 60)**2 / 2) + startDelay; //Score view time to piece time
      setCurrentTime(newTime);
      if (onCurrentTimeChange) onCurrentTimeChange(newTime);
      const newProgress = (newTime / totalDurationRef.current) * 100;
      setProgress(newProgress);
      
      if (wasPlaying) {
        playFromProgress(newProgress);
      }
    },
    bpmScalingFactor,
    displayFactor: ((bpm / 60)**2 / 2)
  }));

  return (
    <div className={`audio-player ${fixed ? 'audio-player--fixed' : 'audio-player--inline'}`}>
      <div className="top-row">
        <div className="piece-name">
          {(() => {
            const parts = pieceDisplayName.split(' - ');
            const composer = parts[0] || '';
            const title = (parts[1] || '').replace(/\.(musicxml|mxl|xml)$/i, '').replace(/_/g, ' ').replace(/-/g, ' ');
            return (
              <>
                <h3 style={{ fontSize: 'larger' }}>{title}</h3>
                <h3 
                  className="composer-link"
                  onClick={() => window.location.assign(`/?query=${encodeURIComponent(composer)}`)}
                >
                  {composer}
                </h3>
              </>
            );
          })()}
        </div>
        <div className="play-pause">
          <button onClick={togglePlayPause} disabled={!midiBlob || isMidiLoading || !midiFile || isNotesLoading} className="play-pause-button">
            <FontAwesomeIcon icon={(isMidiLoading || isNotesLoading) ? faSpinner : (isPlaying ? faPause : faPlay)} spin={isMidiLoading || isNotesLoading} />
          </button>
        </div>
        <button
          className={`player-favorite-button ${isFavorited && isFavorited(selectedPiece) ? 'favorited' : ''}`}
          aria-label={isFavorited && isFavorited(selectedPiece) ? 'Remove favorite' : 'Add favorite'}
          onClick={() => {
            if (isFavorited && isFavorited(selectedPiece)) removeFavorite(selectedPiece);
            else addFavorite(selectedPiece);
          }}
        >
          <FontAwesomeIcon icon={faHeartSolid} />
        </button>

        <div className="bpm-slider-container">
          <label htmlFor="bpm-slider">BPM: {bpm}</label>
          <input
            type="range"
            id="bpm-slider"
            min="20"
            max="240"
            value={bpm}
            onChange={handleBpmChange}
          />
        </div>
      </div>
      <div className="bottom-row">
        <div className="select-container">
          <select value={selectedPart} onChange={handlePartSelection} disabled={!selectedPiece}>
            <option value="">Select a Part</option>
            {parts.map((part, index) => (
              <option key={part.id || index} value={part.id}>{part.name}</option>
            ))}
          </select>
        </div>
        <div className="progress-container">
          <span className="time current-time">{formatTime(currentTime)}</span>
          <input
            type="range"
            min="0"
            max="100"
            step="0.05"
            value={progress}
            onChange={handleProgressChange}
            className="progress-bar"
          />
          <span className="time total-time">{formatTime(totalDuration)}</span>
        </div>
      </div>
    </div>
  );
});

export default AudioPlayer;